import { useState, useCallback } from 'react';
import { apiService } from '../services/api';
import type { MeetingCard, MeetingDetailResponse, MeetingFilters } from '../types';

/**
 * useMeetings Hook
 * Toplantı listesi ve detay işlemlerini yönetir
 */
export function useMeetings() {
  const [meetings, setMeetings] = useState<MeetingCard[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toplantıları getir
  const fetchMeetings = useCallback(async (filters?: MeetingFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getMeetings(filters);
      
      if (response.success && response.data) {
        let filteredMeetings = response.data.meetings;
        
        // Client-side arama
        if (filters?.searchQuery) {
          const query = filters.searchQuery.toLowerCase();
          filteredMeetings = filteredMeetings.filter(m => 
            m.subject.toLowerCase().includes(query) ||
            m.organizer.toLowerCase().includes(query)
          );
        }
        
        setMeetings(filteredMeetings);
      } else {
        setError(response.error?.message || 'Toplantılar yüklenemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Toplantı detayını getir (sadece transkript - analiz yapmadan)
  const fetchMeetingDetail = useCallback(async (meetingId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getMeetingDetail(meetingId, false, false);
      
      if (response.success && response.data) {
        setSelectedMeeting(response.data);
      } else {
        setError(response.error?.message || 'Toplantı detayı yüklenemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // AI analizi oluştur (Özet Çıkar butonu için)
  const generateAnalysis = useCallback(async (meetingId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // analyze=true ile çağır - AI analizi yapılacak
      const response = await apiService.getMeetingDetail(meetingId, true, false);
      
      if (response.success && response.data) {
        setSelectedMeeting(response.data);
      } else {
        setError(response.error?.message || 'Analiz oluşturulamadı');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Yeniden analiz et (cache'i yoksay)
  const reanalyze = useCallback(async (meetingId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // reanalyze=true ile çağır - cache yoksayılacak
      const response = await apiService.getMeetingDetail(meetingId, true, true);
      
      if (response.success && response.data) {
        setSelectedMeeting(response.data);
      } else {
        setError(response.error?.message || 'Yeniden analiz yapılamadı');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Dışa aktar
  const exportMeeting = useCallback(async (
    meetingId: string,
    format: 'pdf' | 'docx',
    includeTranscript = false
  ) => {
    try {
      const response = await apiService.exportMeeting(meetingId, format, includeTranscript);
      
      if (response.success && response.data) {
        // Blob'u indir
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Backend'den gelen filename'i kullan, yoksa fallback
        a.download = (blob as any).filename || `toplanti-raporu.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        return true;
      } else {
        setError(response.error?.message || 'Dışa aktarma başarısız');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'Dışa aktarma hatası');
      return false;
    }
  }, []);

  // Seçili toplantıyı temizle
  const clearSelectedMeeting = useCallback(() => {
    setSelectedMeeting(null);
  }, []);

  // Yeni toplantıları kontrol et (Graph API'den forceSync ile)
  const checkNewMeetings = useCallback(async (filters?: MeetingFilters) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getMeetings(filters, true); // forceSync = true

      if (response.success && response.data) {
        let filteredMeetings = response.data.meetings;

        // Client-side arama
        if (filters?.searchQuery) {
          const query = filters.searchQuery.toLowerCase();
          filteredMeetings = filteredMeetings.filter(m =>
            m.subject.toLowerCase().includes(query) ||
            m.organizer.toLowerCase().includes(query)
          );
        }

        setMeetings(filteredMeetings);
      } else {
        setError(response.error?.message || 'Toplantılar yüklenemedi');
      }
    } catch (err: any) {
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    meetings,
    selectedMeeting,
    isLoading,
    error,
    fetchMeetings,
    fetchMeetingDetail,
    generateAnalysis,
    reanalyze,
    exportMeeting,
    checkNewMeetings,
    clearSelectedMeeting,
  };
}
